import { callable } from '../callable';

export interface ProgramDocumentRequirement {
  type: string;
  required: boolean;
  description?: string;
}

export interface CreateProgramRequest {
  tenantId: string;
  name: string;
  code: string;
  /** Department the program belongs to (single department per program). */
  departmentId?: string;
  durationTerms: number;
  documentRequirements?: ProgramDocumentRequirement[];
}

export interface OpenTermRequest {
  tenantId: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface CreateCourseRequest {
  tenantId: string;
  /** @deprecated programId on Course is legacy. The program association now lives on Section. */
  programId?: string;
  name: string;
  code: string;
  credits: number;
}

export interface CreateSectionRequest {
  tenantId: string;
  programId: string;
  courseId: string;
  termId: string;
  facultyId?: string;
  maxEnrollment?: number;
  sectionCode?: string;
  schedule?: SectionScheduleEntry[];
}

/** Un bloque del horario de una sección. Horas de reloj (HH:mm) en la zona
 *  horaria del seminario. */
export interface SectionScheduleEntry {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  location?: string;
}

// ─── Update & Delete Interfaces ────────────────────────────────────────────

export interface ProgramReviewerConfig {
  personId: string;
  required: boolean;
}

export interface ProgramFeeConfig {
  enrollmentFee: number;
  programTotalFee: number;
  currency: string;
  /** Optional. When set, tuition is calculated per credit × this value. */
  pricePerCredit?: number;
  /** Flat fee per course (used when chargeBasis = 'per_course'). */
  ratePerCourse?: number;
  /**
   * Charge basis. Supersedes the legacy `pricingMode`: per_term (flat, every
   * term) / per_year (flat, once per year) / per_course / per_credit.
   * 'per_program' is the old name of per_year, still readable.
   */
  chargeBasis?: 'per_year' | 'per_program' | 'per_term' | 'per_course' | 'per_credit';
  /** per_year only: what counts as a year. Absent → 'calendar'. */
  annualYearBasis?: 'calendar' | 'program';
  /** LEGACY — kept for back-compat; prefer chargeBasis. */
  pricingMode?: 'per-credit' | 'fixed';
  /**
   * When the charge is collected: `immediate` (enroll now, bill after) or
   * `pay_to_enroll` (the online checkout conditions the enrollment). Only takes
   * effect when the tenant collects with an online provider (e.g. Mercado Pago).
   */
  paymentMode?: 'immediate' | 'pay_to_enroll';
}

export interface RubricLevelDto {
  id: string;
  name: string;
  description: string;
}
export interface RubricCriterionDto {
  id: string;
  label: string;
  description: string;
  weight: number;
  levels?: RubricLevelDto[];
}
export interface RubricProvenanceDto {
  programId: string;
  programName: string;
  copiedAt: string;
}

export interface RubricDto {
  id: string;
  title: string;
  criteria: RubricCriterionDto[];
  copiedFrom?: RubricProvenanceDto;
}

/** Phase D — one recommended term in a curriculum version. */
export interface CurriculumTermPlanDto {
  /** 0-indexed: 0 = first term in the program. */
  termOffset: number;
  label?: string;
  recommendedCourseIds: string[];
  /** "pick N from electives" — informational for the audit. */
  electiveSlots?: number;
  /** El año de la escalera del centro al que pertenece este término. Es lo que
   *  permite que dos programas que se cruzan compartan columna en la escalera.
   *  Ver `CurriculumTermPlan` en el dominio. */
  year?: number;
  /** La mitad del año, 1 o 2. Ausente = el término ocupa el año entero. */
  semester?: number;
}

/** Phase D — recommended course sequence for a given catalogYear. */
export interface ProgramCurriculumVersionDto {
  catalogYear: string;
  terms: CurriculumTermPlanDto[];
  /** Absent = active. A discontinued plan is no longer offered to new students
   *  but stays valid for whoever is pinned to it (teach-out). */
  status?: 'active' | 'discontinued';
}

/** The course a student approved and the one it satisfies after a reform. */
export interface CourseEquivalenceDto {
  fromCourseId: string;
  toCourseId: string;
  note?: string;
}

export interface UpdateProgramRequest extends Partial<Omit<CreateProgramRequest, 'tenantId'>> {
  tenantId: string;
  programId: string;
  description?: string;
  purpose?: string;
  status?: 'active' | 'inactive' | 'archived';
  formTemplateId?: string | null;
  documentRequirements?: ProgramDocumentRequirement[];
  reviewerConfig?: ProgramReviewerConfig[];
  feeConfig?: ProgramFeeConfig;
  /** H4: el programa se declara «sin arancel» (no crea plan de pago). */
  noTuition?: boolean;
  /** Hex color (e.g. "#7C3AED") used as accent in templates dashboard. */
  primaryColor?: string;
  /** Minimum attendance % for good standing (null clears; empty = default). */
  minAttendancePct?: number | null;
  /** FK to Department (Phase B). */
  departmentId?: string;
  /** Phase C.1 multi-rubric catalog. */
  rubrics?: RubricDto[];
  /** Phase D — versioned recommended course sequence per catalogYear. */
  curriculumVersions?: ProgramCurriculumVersionDto[];
  courseEquivalences?: CourseEquivalenceDto[];
  /** Phase D — catalogYear assigned to NEW enrollments (live catalog). */
  currentCatalogYear?: string | null;
  /** Entrega 3A — hard-block enrollment on unmet prerequisites. */
  enforcePrerequisites?: boolean;
  /** Entrega 3B — minimum courses per term (null clears). */
  minCoursesPerTerm?: number | null;
  /** Entrega 2 — offering mode. */
  offeringMode?: 'open_catalog' | 'curated_cohort';
  /** The TMAI CAP this program targets (1:1). null clears it. */
  capTarget?: { capCode: 'BSC' | 'DEP'; level: 'undergraduate' | 'graduate' } | null;
  /** OAPs — program-level learning objectives (source of truth for the syllabus). */
  learningObjectives?: DepartmentObjectiveDto[];
  /** Program teaching philosophy commitments (source of truth for the syllabus). */
  philosophy?: { id: string; title: string; description: string }[];
}

// ─── Department (Phase B) ──────────────────────────────────────────────────

export interface DepartmentObjectiveDto {
  id: string;
  code?: string;
  text: string;
}

export interface CreateDepartmentRequest {
  tenantId: string;
  name: string;
  code: string;
  purpose?: string;
  learningObjectives?: DepartmentObjectiveDto[];
  primaryColor?: string;
}

export interface UpdateDepartmentRequest {
  tenantId: string;
  departmentId: string;
  name?: string;
  code?: string;
  purpose?: string;
  learningObjectives?: DepartmentObjectiveDto[];
  primaryColor?: string;
  status?: 'active' | 'inactive';
}

export interface DeleteDepartmentRequest {
  tenantId: string;
  departmentId: string;
}

export interface DeleteProgramRequest {
  tenantId: string;
  programId: string;
}

export interface UpdateTermRequest extends Partial<Omit<OpenTermRequest, 'tenantId'>> {
  tenantId: string;
  termId: string;
  enrollmentDeadline?: string;
  /** Entrega 2: enrollment window (both dates) or null to clear. */
  enrollmentWindow?: { opensAt: string; closesAt: string } | null;
  /** Entrega 2 (latent): per-term offering-mode override. */
  offeringModeOverride?: 'open_catalog' | 'curated_cohort' | null;
  status?: 'planned' | 'open' | 'in_progress' | 'closed';
}

export interface DeleteTermRequest {
  tenantId: string;
  termId: string;
}

export interface HardDeleteTermRequest {
  tenantId: string;
  termId: string;
}

export interface CourseObjectiveDto {
  id: string;
  text: string;
  code?: string;
}

export interface CourseAnchorVerseDto {
  reference: string;
  text: string;
  translation: string;
}

export interface CourseTextbookRefDto {
  textbookId: string;
  notes?: string;
}

export interface UpdateCourseRequest extends Partial<
  Omit<CreateCourseRequest, 'tenantId' | 'programId'>
> {
  tenantId: string;
  courseId: string;
  description?: string;
  learningObjectives?: CourseObjectiveDto[];
  anchorVerse?: CourseAnchorVerseDto | null;
  biblicalVersion?: string;
  recommendedReadings?: CourseTextbookRefDto[];
  prerequisites?: string[];
  status?: 'active' | 'inactive' | 'archived';
  /** Legacy FK to Program. Pass `null` to clear, string to set. */
  programId?: string | null;
}

export interface DeleteCourseRequest {
  tenantId: string;
  courseId: string;
}

export interface UpdateSectionRequest {
  tenantId: string;
  sectionId: string;
  facultyId?: string;
  /** Reemplaza la lista completa de ayudantes. Vacía los quita a todos;
   *  omitirla no los toca. */
  assistantIds?: string[];
  maxEnrollment?: number;
  status?: 'open' | 'closed' | 'cancelled' | 'grades_closed';
  startDate?: string | null;
  endDate?: string | null;
  /** Change the program assignment — moves the course to another program within the period. */
  programId?: string;
  /** El código de la sección. Único dentro del período (lo valida el servidor). */
  sectionCode?: string;
  /** Reemplaza el horario COMPLETO. Omitirlo no lo toca: así un horario
   *  importado que el editor no sabe leer no se reescribe al guardar. */
  schedule?: SectionScheduleEntry[];
}

export interface DeleteSectionRequest {
  tenantId: string;
  sectionId: string;
}

export interface HardDeleteSectionRequest {
  tenantId: string;
  sectionId: string;
}

export interface SetSectionLifecycleRequest {
  tenantId: string;
  sectionId: string;
  /** `null` clears the override and resumes date-based derivation. */
  override: 'active' | 'concluded' | null;
}

export const academicApi = {
  createProgram: async (data: CreateProgramRequest) => {
    const fn = callable<CreateProgramRequest, { id: string }>('createProgram');
    const result = await fn(data);
    return result.data;
  },

  createTerm: async (data: OpenTermRequest) => {
    const fn = callable<OpenTermRequest, { id: string }>('createTerm');
    const result = await fn(data);
    return result.data;
  },

  createCourse: async (data: CreateCourseRequest) => {
    const fn = callable<CreateCourseRequest, { id: string }>('createCourse');
    const result = await fn(data);
    return result.data;
  },

  createSection: async (data: CreateSectionRequest) => {
    const fn = callable<CreateSectionRequest, { id: string }>('createSection');
    const result = await fn(data);
    return result.data;
  },

  generateTermSections: async (data: {
    tenantId: string;
    programId: string;
    termId: string;
    courseIds?: string[];
    maxEnrollment?: number;
  }) => {
    const fn = callable<typeof data, { created: number; skipped: number; sectionIds: string[] }>(
      'generateTermSections',
    );
    return (await fn(data)).data;
  },

  updateProgram: async (data: UpdateProgramRequest) => {
    const fn = callable<UpdateProgramRequest, { success: boolean }>('updateProgram');
    const result = await fn(data);
    return result.data;
  },

  // Dedicated callable for the minimum attendance threshold — updateProgram's
  // gen2 deploy would not pick up the new schema field, so this ships it.
  setProgramMinAttendance: async (data: {
    tenantId: string;
    programId: string;
    minAttendancePct: number | null;
  }) => {
    const fn = callable<typeof data, { success: boolean }>('setProgramMinAttendance');
    return (await fn(data)).data;
  },

  // A program's place in a ladder: what it builds on (null = nothing), whether
  // entering requires having completed that, and whether the award adds up the
  // chain below it.
  setProgramLadderRules: async (data: {
    tenantId: string;
    programId: string;
    stacksOnProgramId: string | null;
    enforceStackEntry: boolean;
    includesStackedPrograms: boolean;
  }) => {
    const fn = callable<typeof data, { success: boolean }>('setProgramLadderRules');
    return (await fn(data)).data;
  },

  // ─── Department (Phase B) ───────────────────────────────────────────────
  createDepartment: async (data: CreateDepartmentRequest): Promise<{ id: string }> => {
    const fn = callable<CreateDepartmentRequest, { id: string }>('createDepartment');
    return (await fn(data)).data;
  },
  updateDepartment: async (data: UpdateDepartmentRequest): Promise<{ ok: true }> => {
    const fn = callable<UpdateDepartmentRequest, { ok: true }>('updateDepartment');
    return (await fn(data)).data;
  },
  deleteDepartment: async (data: DeleteDepartmentRequest): Promise<{ ok: true }> => {
    const fn = callable<DeleteDepartmentRequest, { ok: true }>('deleteDepartment');
    return (await fn(data)).data;
  },

  deleteProgram: async (data: DeleteProgramRequest) => {
    const fn = callable<DeleteProgramRequest, { success: boolean }>('deleteProgram');
    const result = await fn(data);
    return result.data;
  },

  updateTerm: async (data: UpdateTermRequest) => {
    const fn = callable<UpdateTermRequest, { success: boolean }>('updateTerm');
    const result = await fn(data);
    return result.data;
  },

  deleteTerm: async (data: DeleteTermRequest) => {
    const fn = callable<DeleteTermRequest, { success: boolean }>('deleteTerm');
    const result = await fn(data);
    return result.data;
  },

  hardDeleteTerm: async (data: HardDeleteTermRequest) => {
    const fn = callable<HardDeleteTermRequest, { success: boolean }>('hardDeleteTerm');
    const result = await fn(data);
    return result.data;
  },

  updateCourse: async (data: UpdateCourseRequest) => {
    const fn = callable<UpdateCourseRequest, { success: boolean }>('updateCourse');
    const result = await fn(data);
    return result.data;
  },

  deleteCourse: async (data: DeleteCourseRequest) => {
    const fn = callable<DeleteCourseRequest, { success: boolean }>('deleteCourse');
    const result = await fn(data);
    return result.data;
  },

  updateSection: async (data: UpdateSectionRequest) => {
    const fn = callable<UpdateSectionRequest, { success: boolean }>('updateSection');
    const result = await fn(data);
    return result.data;
  },

  deleteSection: async (data: DeleteSectionRequest) => {
    const fn = callable<DeleteSectionRequest, { success: boolean }>('deleteSection');
    const result = await fn(data);
    return result.data;
  },

  hardDeleteSection: async (data: HardDeleteSectionRequest) => {
    const fn = callable<HardDeleteSectionRequest, { success: boolean }>('hardDeleteSection');
    const result = await fn(data);
    return result.data;
  },

  setSectionLifecycle: async (data: SetSectionLifecycleRequest) => {
    const fn = callable<SetSectionLifecycleRequest, { success: boolean }>('setSectionLifecycle');
    const result = await fn(data);
    return result.data;
  },

  /** Docente e inscritos de cada sección, sin datos de alumnos (PR 3A). Por
   *  período, o por ids (hasta 100: ver `fetchSectionsOverview`). */
  getTermSectionsOverview: async (
    data: ({ tenantId: string; termId: string } | { tenantId: string; sectionIds: string[] }) & {
      /** `false` = sólo nombres, sin contar inscritos (más barato). */
      counts?: boolean;
    },
  ): Promise<{ sections: TermSectionOverviewDto[]; studentCount: number | null }> => {
    const fn = callable<
      typeof data,
      { sections: TermSectionOverviewDto[]; studentCount: number | null }
    >('getTermSectionsOverview');
    return (await fn(data)).data;
  },

  /** La nómina de UNA sección: gestión, o quien enseña en ella (PR 3A). */
  listSectionRoster: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ sectionId: string; rows: SectionRosterRowDto[] }> => {
    const fn = callable<typeof data, { sectionId: string; rows: SectionRosterRowDto[] }>(
      'listSectionRoster',
    );
    return (await fn(data)).data;
  },
};

export interface TermSectionOverviewDto {
  sectionId: string;
  /** El titular; `null` si no tiene o su ficha no se lee. */
  facultyName: string | null;
  /** Inscritos según la regla única (todo menos retiros); `null` si se pidió
   *  sin conteos. */
  enrolledCount: number | null;
}

export interface SectionRosterRowDto {
  enrollmentId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  /** Sólo una `enrolled` se puede retirar o mover. */
  status: string;
}
